/**
 * Presentation-currency translation for the balance sheet (IFRS SME Section
 * 30) — moved verbatim out of `apps/web/src/hooks/useTranslatedBalanceSheet.ts`
 * so the server-side document assembler and the on-screen report run the exact
 * same CTA-vs-unexplained-difference branching.
 *
 * The hook keeps its TanStack Query glue and re-exports this function from its
 * own module path, so its existing tests (and any other importer) are
 * untouched.
 */
import { translateAmount } from "../currencyTranslation.js";
import { roundCents, sumCents } from "../ledgerReports.js";
/**
 * Pure assembler for the translated balance sheet — extracted from the hook so
 * the plug/branching logic is unit-testable without React or Supabase.
 *
 * The `Assets − Liabilities − Equity` plug means two completely different
 * things depending on whether the org actually holds foreign currency, and
 * the two must never be conflated:
 *
 * - **Foreign currency present** (`hasForeignCurrency`): the plug is a
 *   legitimate Cumulative Translation Adjustment (IFRS SME 30.18(c)). It also
 *   deliberately absorbs the residual of the excluded `fx_clearing` system
 *   account, whose entries span currencies (see the rationale in
 *   `supabase/migrations/20260728130000_balance_sheet_include_suspense.sql`).
 *   It is folded into `totalEquity` and rendered as a CTA equity line.
 *
 * - **Single currency**: `translateAmount` is the identity, so a non-zero plug
 *   is NOT a translation adjustment — it is a genuine out-of-balance condition
 *   (data corruption). It is surfaced as `unexplainedDifference`, is NOT
 *   folded into `totalEquity`, and `totalLiabilitiesAndEquity` honestly
 *   diverges from `totalAssets` so the report's balance check fires.
 *
 * Foreign-currency presence is decided from `nativeData.accountCurrencies`
 * (all currencies in the RAW rows, before zero-balance accounts are dropped)
 * — deriving it from the assembled line items would misclassify an org whose
 * foreign-currency accounts are zero on the as-of date but which still carries
 * an fx_clearing residual.
 */
export function assembleTranslatedBalanceSheet({ nativeData, rates, ratesArray, presentationCurrency, asOfDate, }) {
    const presentationCurrencyUpper = presentationCurrency.toUpperCase();
    // Helper to translate a balance sheet item
    const translateItem = (item) => {
        const { translatedAmount, rate, rateDate } = translateAmount(item.amount, item.currency, rates, presentationCurrencyUpper, asOfDate);
        return {
            name: item.name,
            // Copied through so a comparative column can pair rows by account
            // identity rather than by (non-unique) name. Spread conditionally so an
            // id-less item (the synthetic result lines) keeps its exact previous
            // shape instead of gaining an explicit `id: undefined` key.
            ...(item.id === undefined ? {} : { id: item.id }),
            ...(item.kind === undefined ? {} : { kind: item.kind }),
            nativeAmount: item.amount,
            nativeCurrency: item.currency,
            // Rounded once here (a foreign amount × rate has more than two
            // decimals), so every total below is the sum of the printed figures.
            translatedAmount: roundCents(translatedAmount),
            presentationCurrency: presentationCurrencyUpper,
            exchangeRate: rate,
            rateDate,
        };
    };
    // Helper to sum translated amounts
    const sumTranslated = (items) => {
        return sumCents(items.map((item) => item.translatedAmount));
    };
    // Translate all items
    const translatedAssetsCurrent = nativeData.assets.current.map(translateItem);
    const translatedAssetsNonCurrent = nativeData.assets.nonCurrent.map(translateItem);
    const translatedLiabilitiesCurrent = nativeData.liabilities.current.map(translateItem);
    const translatedLiabilitiesNonCurrent = nativeData.liabilities.nonCurrent.map(translateItem);
    const translatedEquity = nativeData.equity.map(translateItem);
    // Calculate totals
    const currentAssets = sumTranslated(translatedAssetsCurrent);
    const nonCurrentAssets = sumTranslated(translatedAssetsNonCurrent);
    const totalAssets = roundCents(currentAssets + nonCurrentAssets);
    const currentLiabilities = sumTranslated(translatedLiabilitiesCurrent);
    const nonCurrentLiabilities = sumTranslated(translatedLiabilitiesNonCurrent);
    const totalLiabilities = roundCents(currentLiabilities + nonCurrentLiabilities);
    const totalEquityBeforeCTA = sumTranslated(translatedEquity);
    // Unique functional currencies (excluding presentation currency), from the
    // RAW rows — see the function doc comment for why pre-drop matters.
    const functionalCurrencies = [
        ...new Set(nativeData.accountCurrencies
            .map((c) => c.toUpperCase())
            .filter((c) => c !== presentationCurrencyUpper)),
    ].sort();
    const hasForeignCurrency = functionalCurrencies.length > 0;
    // The balancing plug: Assets − Liabilities − Equity, rounded to cents
    // (`roundCents` also normalizes -0 to 0 when the plug is negative noise).
    const roundedPlug = roundCents(totalAssets - totalLiabilities - totalEquityBeforeCTA);
    // Branch on foreign-currency presence — see the function doc comment.
    const ctaAmount = hasForeignCurrency ? roundedPlug : 0;
    const unexplainedDifference = hasForeignCurrency ? 0 : roundedPlug;
    const totalEquity = roundCents(totalEquityBeforeCTA + ctaAmount);
    const totalLiabilitiesAndEquity = roundCents(totalLiabilities + totalEquity);
    return {
        presentationCurrency: presentationCurrencyUpper,
        closingDate: asOfDate,
        assets: {
            current: translatedAssetsCurrent,
            nonCurrent: translatedAssetsNonCurrent,
        },
        liabilities: {
            current: translatedLiabilitiesCurrent,
            nonCurrent: translatedLiabilitiesNonCurrent,
        },
        equity: translatedEquity,
        totals: {
            currentAssets,
            nonCurrentAssets,
            totalAssets,
            currentLiabilities,
            nonCurrentLiabilities,
            totalLiabilities,
            totalEquityBeforeCTA,
            totalEquity,
            totalLiabilitiesAndEquity,
        },
        hasForeignCurrency,
        cumulativeTranslationAdjustment: {
            amount: ctaAmount,
            currency: presentationCurrencyUpper,
            description: 'Cumulative Translation Adjustment',
        },
        unexplainedDifference,
        disclosures: {
            exchangeRatesUsed: ratesArray,
            translationMethod: 'closing_rate',
            functionalCurrencies,
            presentationCurrency: presentationCurrencyUpper,
            closingDate: asOfDate,
        },
        sourceData: nativeData,
    };
}
//# sourceMappingURL=translate.js.map